/**
 * Converts watchlist filter store data into the API payload structure.
 * Follows the same key naming conventions used by viewInventoryHandler.js
 * and columnConfigUtils.js preprocessFilters.
 */

const BOND_TYPE_TO_API = {
  Treasury: "Gov",
  Corporates: "Corp",
  Municipals: "Muni",
};

export const mapWatchlistBondTypeToApiType = (bondType) =>
  BOND_TYPE_TO_API[bondType] || bondType;

const WATCHLIST_FILTER_FIELDS_BY_BOND_TYPE = {
  Treasury: [
    "securityCharge",
    "couponRange",
    "callable",
    "maturityRanges",
    "conventionMethod",
    "couponType",
    "currency",
    "incomeSource",
    "issueCurrency",
    "subType",
    "issuer",
    "insured",
    "perpetual",
    "zeroCoupon",
  ],
  Municipals: [
    "securityCharge",
    "couponRange",
    "callable",
    "minimumRating",
    "minimumRatingMoody",
    "maturityRanges",
    "conventionMethod",
    "couponType",
    "currency",
    "state",
    "incomeSource",
    "issueCurrency",
    "subType",
    "issuer",
    "paymentFrequency",
    "callStartDate",
    "insured",
    "perpetual",
    "zeroCoupon",
  ],
  Corporates: [
    "securityCharge",
    "couponRange",
    "callable",
    "minimumRating",
    "minimumRatingMoody",
    "maturityRanges",
    "conventionMethod",
    "couponType",
    "currency",
    "issueCurrency",
    "subType",
    "paymentFrequency",
    "incomeSource",
    "issuer",
    "sector",
    "callStartDate",
    "nextCallDate",
    "insured",
    "perpetual",
    "zeroCoupon",
  ],
};

const WATCHLIST_COMPOSITE_FIELDS = {
  couponRange: ["couponMin", "couponMax", "minCoupon", "maxCoupon"],
  maturityRanges: ["maturityMin", "maturityMax", "minMaturity", "maxMaturity"],
};

const WATCHLIST_FETCH_FILTER_KEYS = new Set([
  "type",
  "side",
  "minMaturity",
  "maxMaturity",
  "callStartDate",
  "nextCallDate",
  "minDuration",
  "maxDuration",
  "minPrice",
  "maxPrice",
  "minYtm",
  "maxYtm",
  "minCoupon",
  "maxCoupon",
  "insured",
  "perpetual",
  "taxable",
  "altMinTax",
  "zeroCoupon",
  "state",
  "paymentFrequency",
  "sector",
  "incomeSource",
  "callable",
  "issuer",
  "conventionMethod",
  "couponType",
  "currency",
  "issueCurrency",
  "securityCharge",
  "subType",
  "minRatingSP",
  "maxRatingSP",
  "minRatingMoody",
  "maxRatingMoody",
]);

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

const SECURITY_CHARGE_ALL = "ALL";

// Mirrors the order-entry "Factored Securities" contract:
// an array containing "ALL" serializes to the string "ALL", a specific
// selection stays an array, and an empty value is omitted from the payload.
const serializeSecurityCharge = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return undefined;
    }

    return value.includes(SECURITY_CHARGE_ALL) ? SECURITY_CHARGE_ALL : value;
  }

  return hasValue(value) ? value : undefined;
};

const areEqualFilterValues = (left, right) => {
  if (!hasValue(left) && !hasValue(right)) {
    return true;
  }

  if (!hasValue(left) || !hasValue(right)) {
    return false;
  }

  return `${left}` === `${right}`;
};

const normalizeYesNoToYN = (value) => {
  if (!hasValue(value)) {
    return value;
  }

  const normalizedValue = `${value}`.trim().toLowerCase();

  // Watchlist boolean columns are backed by SQL char flags, so submit Y/N.
  if (value === true || normalizedValue === "yes" || normalizedValue === "y" || normalizedValue === "1") {
    return "Y";
  }

  if (value === false || normalizedValue === "no" || normalizedValue === "n" || normalizedValue === "0") {
    return "N";
  }

  return value;
};

const normalizeMaturityYears = (value) => {
  if (!hasValue(value)) {
    return value;
  }

  const stringValue = `${value}`.trim();
  if (
    stringValue.endsWith("Y")
    || stringValue.includes("T")
    || /^\d{4}-\d{2}-\d{2}$/.test(stringValue)
  ) {
    return stringValue;
  }

  return `${stringValue}Y`;
};

const addDurationBounds = (filters) => {
  if (!hasValue(filters.duration)) {
    return;
  }

  const durationValue = `${filters.duration}`.replace(/[<>=]/g, "").trim();
  if (!durationValue || Number.isNaN(Number(durationValue))) {
    return;
  }

  if (`${filters.duration}`.startsWith(">=")) {
    filters.minDuration = Number(durationValue);
    return;
  }

  if (`${filters.duration}`.startsWith("<=")) {
    filters.maxDuration = Number(durationValue);
  }
};

const toWatchlistFetchBackendFilters = (filters = {}) => {
  const backendFilters = { ...filters };

  if (hasValue(backendFilters.maturityMin)) {
    backendFilters.minMaturity = normalizeMaturityYears(backendFilters.maturityMin);
  }
  if (hasValue(backendFilters.maturityMax)) {
    backendFilters.maxMaturity = normalizeMaturityYears(backendFilters.maturityMax);
  }

  addDurationBounds(backendFilters);

  ["callable", "insured", "perpetual", "taxable", "altMinTax", "zeroCoupon"].forEach((key) => {
    if (hasValue(backendFilters[key])) {
      backendFilters[key] = normalizeYesNoToYN(backendFilters[key]);
    }
  });

  return backendFilters;
};

const keepWatchlistFetchFilterKeys = (filters = {}) =>
  Object.entries(filters).reduce((accumulator, [key, value]) => {
    if (WATCHLIST_FETCH_FILTER_KEYS.has(key)) {
      accumulator[key] = value;
    }

    return accumulator;
  }, {});

const getWatchlistAllowedKeys = (bondType) => {
  const allowed = new Set(["bondType", "type"]);
  const fields = WATCHLIST_FILTER_FIELDS_BY_BOND_TYPE[bondType] || [];

  fields.forEach((field) => {
    if (WATCHLIST_COMPOSITE_FIELDS[field]) {
      WATCHLIST_COMPOSITE_FIELDS[field].forEach((key) => allowed.add(key));
      return;
    }

    allowed.add(field);
  });

  if (allowed.has("minimumRating")) {
    allowed.add("minRatingSP");
    allowed.add("maxRatingSP");
  }

  if (allowed.has("minimumRatingMoody")) {
    allowed.add("minRatingMoody");
    allowed.add("maxRatingMoody");
  }

  return allowed;
};

const filterWatchlistObjectByBondType = (bondType, object = {}) => {
  const allowed = getWatchlistAllowedKeys(bondType);

  return Object.entries(object).reduce((accumulator, [key, value]) => {
    if (allowed.has(key)) {
      accumulator[key] = value;
    }

    return accumulator;
  }, {});
};

export const hasWatchlistCriteria = (criteria = {}) =>
  Object.values(criteria || {}).some(hasValue);

export const buildWatchlistFilterPayload = (filterData = {}) => {
  const {
    bondType,
    securityCharge,
    side,
    couponMin,
    couponMax,
    maturityMin,
    maturityMax,
    priceMin,
    priceMax,
    yieldMin,
    yieldMax,
    duration,
    minimumRating,
    minimumRatingMoody,
    zeroCoupon,
    perpetual,
    callable,
    state,
    insured,
    taxable,
    paymentFrequency,
    incomeSource,
    altMinTax,
    sector,
    callStartDate,
    nextCallDate,
    issuer,
    conventionMethod,
    couponType,
    currency,
    issueCurrency,
    subType,
  } = filterData;

  const type = mapWatchlistBondTypeToApiType(bondType);
  const serializedSecurityCharge = serializeSecurityCharge(securityCharge);

  const filters = {
    type,
    ...(serializedSecurityCharge !== undefined && {
      securityCharge: serializedSecurityCharge,
    }),
    ...(hasValue(side) && { side }),
    ...(hasValue(maturityMin) && { maturityMin }),
    ...(hasValue(maturityMax) && { maturityMax }),
    ...(hasValue(callStartDate) && { callStartDate }),
    ...(hasValue(nextCallDate) && { nextCallDate }),
    ...(hasValue(duration) && { duration }),
    ...(hasValue(priceMin) && { minPrice: Number(priceMin) }),
    ...(hasValue(priceMax) && { maxPrice: Number(priceMax) }),
    ...(hasValue(yieldMin) && { minYtm: Number(yieldMin) }),
    ...(hasValue(yieldMax) && { maxYtm: Number(yieldMax) }),
    ...(hasValue(couponMin) && { minCoupon: Number(couponMin) }),
    ...(hasValue(couponMax) && { maxCoupon: Number(couponMax) }),
    ...(hasValue(insured) && { insured }),
    ...(hasValue(perpetual) && { perpetual }),
    ...(hasValue(taxable) && { taxable }),
    ...(hasValue(altMinTax) && { altMinTax }),
    ...(hasValue(zeroCoupon) && { zeroCoupon }),
    ...(hasValue(state) && { state }),
    ...(hasValue(paymentFrequency) && { paymentFrequency }),
    ...(hasValue(sector) && { sector }),
    ...(hasValue(incomeSource) && { incomeSource }),
    ...(hasValue(callable) && { callable }),
    ...(hasValue(issuer) && { issuer }),
    ...(hasValue(conventionMethod) && { conventionMethod }),
    ...(hasValue(couponType) && { couponType }),
    ...(hasValue(currency) && { currency }),
    ...(hasValue(issueCurrency) && { issueCurrency }),
    ...(hasValue(subType) && { subType }),
    ...(hasValue(minimumRating) && {
      minRatingSP: minimumRating,
      maxRatingSP: "AAA",
    }),
    ...(hasValue(minimumRatingMoody) && {
      minRatingMoody: minimumRatingMoody,
      maxRatingMoody: "Aaa",
    }),
  };

  return keepWatchlistFetchFilterKeys(
    toWatchlistFetchBackendFilters(filterWatchlistObjectByBondType(bondType, filters))
  );
};

export const buildWatchlistCharacteristicsPayload = (filterData = {}) => {
  // Keep persisted dynamic criteria keys aligned with fetch payload keys
  // (same contract as applyInventoryFilters-generated API fields).
  return {
    ...buildWatchlistFilterPayload(filterData),
  };
};

const API_TYPE_TO_BOND_TYPE = {
  Gov: "Treasury",
  GOV: "Treasury",
  Muni: "Municipals",
  MUNI: "Municipals",
  Corp: "Corporates",
  CORP: "Corporates",
};

const stripMaturityYearSuffix = (value) => {
  if (!hasValue(value)) return value;
  const stringValue = `${value}`.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue) || stringValue.includes("T")) {
    return stringValue;
  }
  return stringValue.endsWith("Y") ? stringValue.slice(0, -1) : stringValue;
};

const API_KEY_TO_FILTER_FIELD = {
  minCoupon: "couponMin",
  maxCoupon: "couponMax",
  minMaturity: "maturityMin",
  maxMaturity: "maturityMax",
  minPrice: "priceMin",
  maxPrice: "priceMax",
  minYtm: "yieldMin",
  maxYtm: "yieldMax",
  minRatingSP: "minimumRating",
  minRatingMoody: "minimumRatingMoody",
};

const FILTER_FIELD_ALIASES = {
  alt_min_tax: "altMinTax",
  convention_method: "conventionMethod",
  coupon_type: "couponType",
  govState: "state",
  gov_state: "state",
  isAltMinTax: "altMinTax",
  isCallable: "callable",
  income_source: "incomeSource",
  issue_currency: "issueCurrency",
  isInsured: "insured",
  isPerpetual: "perpetual",
  isTaxable: "taxable",
  isZeroCoupon: "zeroCoupon",
  call_start_date: "callStartDate",
  next_call_date: "nextCallDate",
  payment_frequency: "paymentFrequency",
  security_charge: "securityCharge",
  sub_type: "subType",
  is_alt_min_tax: "altMinTax",
  is_callable: "callable",
  is_insured: "insured",
  is_perpetual: "perpetual",
  is_taxable: "taxable",
  is_zero_coupon: "zeroCoupon",
  zero_coupon: "zeroCoupon",
};

const FILTER_FIELDS_PASSTHROUGH = new Set([
  "callable",
  "insured",
  "perpetual",
  "taxable",
  "altMinTax",
  "zeroCoupon",
  "state",
  "paymentFrequency",
  "sector",
  "incomeSource",
  "issuer",
  "callStartDate",
  "nextCallDate",
  "duration",
  "conventionMethod",
  "couponType",
  "currency",
  "issueCurrency",
  "securityCharge",
  "subType",
]);

export const parseWatchlistCharacteristics = (characteristics) => {
  if (!characteristics) return null;

  let parsed = characteristics;
  if (typeof characteristics === "string") {
    const trimmed = characteristics.trim();
    if (!trimmed) return null;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object") return null;

  const filterData = {};

  if (hasValue(parsed.type)) {
    filterData.bondType = API_TYPE_TO_BOND_TYPE[parsed.type] || parsed.type;
  }

  Object.entries(parsed).forEach(([key, value]) => {
    const normalizedKey = FILTER_FIELD_ALIASES[key] || key;

    if (
      normalizedKey === "type"
      || normalizedKey === "maxRatingSP"
      || normalizedKey === "maxRatingMoody"
    ) {
      return;
    }

    if (!hasValue(value)) return;

    if (normalizedKey === "minMaturity" || normalizedKey === "maxMaturity") {
      filterData[API_KEY_TO_FILTER_FIELD[normalizedKey]] = stripMaturityYearSuffix(value);
      return;
    }

    if (API_KEY_TO_FILTER_FIELD[normalizedKey]) {
      filterData[API_KEY_TO_FILTER_FIELD[normalizedKey]] = value;
      return;
    }

    // Factored Securities round-trips as an array for the multi-select field:
    // backend "ALL" string -> ["ALL"], specific string -> [value], array stays.
    if (normalizedKey === "securityCharge") {
      filterData.securityCharge = Array.isArray(value) ? value : [value];
      return;
    }

    if (FILTER_FIELDS_PASSTHROUGH.has(normalizedKey)) {
      filterData[normalizedKey] = value;
    }
  });

  return Object.keys(filterData).length > 0 ? filterData : null;
};

export const buildWatchlistCriteriaRequest = (filterData = {}) => {
  const filters = buildWatchlistFilterPayload(filterData);
  const characteristics = buildWatchlistCharacteristicsPayload(filterData);

  return {
    bondType: filterData.bondType,
    type: filters.type,
    filters,
    characteristics,
    hasCriteria: hasWatchlistCriteria(filters),
  };
};

export const stripWatchlistDefaultFilterValues = (
  filterData = {},
  defaultFilterData = {}
) =>
  Object.entries(filterData || {}).reduce((nextFilterData, [key, value]) => {
    if (!hasValue(value)) {
      return nextFilterData;
    }

    if (areEqualFilterValues(value, defaultFilterData?.[key])) {
      return nextFilterData;
    }

    nextFilterData[key] = value;
    return nextFilterData;
  }, {});

export const buildWatchlistCriteriaState = (filterData = {}, mode = "") => ({
  ...buildWatchlistCriteriaRequest(filterData),
  mode,
});

export const getWatchlistCriteriaComparisonKey = (criteria) =>
  JSON.stringify({
    mode: criteria?.mode || "",
    bondType: criteria?.bondType || "",
    type: criteria?.type || "",
    filters: criteria?.filters || {},
    characteristics: criteria?.characteristics || {},
    hasCriteria: Boolean(criteria?.hasCriteria),
  });
